// eslint-disable-next-line no-unused-vars
import React, { forwardRef, memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { string, func, bool, object, oneOfType } from 'prop-types';
import styled from '@emotion/styled';
import {
  InlineLoading,
  Loading,
  Button,
  Checkbox,
  Search
} from '@carbon/react';
import uniqueId from 'lodash/uniqueId';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { Search as SearchIcon, Package, Megaphone } from 'lucide-react';

import Img from 'Containers/Image/Image';
import AddToCart from 'Components/AddToCart/AddToCart';
import Price from 'Components/Price/Price';
import ProductPriceRequest from 'Containers/Products/ProductPriceRequest';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';
import { renderSearchStock } from 'Components/Products/helpers';
import highlightText from 'Utils/highlightText';

const StyledSearchComponent = styled.div`
  height: 42px;
  max-width: 810px;
  position: relative;
  display: flex;
  flex-grow: 1;
  align-items: center;
  margin-right: 15px;

  @media screen and (max-width: 767px) {
    margin-top: 15px;
    height: 40px;
    width: 100%;
    margin-right: 0;
  }
`;

const StyledSearch = styled(Search)`
  position: relative;
  border-radius: 6px;
  width: 100%;

  .cds--search-input {
    &:focus,
    &:active {
      outline: 0 none;
    }

    height: 42px;
    border-radius: 6px;
    background-color: ${ props => props.theme.colors.white };
    padding-left: 10px;
  }

  .cds--search-magnifier {
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 0;
    height: 100%;
    width: 42px;
    background-color: ${ props => props.theme.colors.default };
    color: ${ props => props.theme.colors.white };

    svg {
      position: relative;
      transform: none;
      top: auto;
      left: auto;
    }
  }

  .cds--search-close {
    right: 45px;
    top: 0;

    &:before,
    &:after {
      display: none;
    }
    &:focus,
    &:hover,
    &:active,
    :visited {
      outline: 0 none !important;
      outline-offset: 0;
      background-color: transparent;
      border: 0 none !important;
      box-shadow: none;
      color: ${ props => props.theme.colors.darkGray };

      svg {
        fill: ${ props => props.theme.colors.darkGray };
        color: ${ props => props.theme.colors.darkGray };
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .cds--search-close {
      right: 55px;
      top: 0;
      height: 40px;
      width: 40px;

      svg {
        height: 24px;
        width: 24px;
      }
    }

    .cds--search-input {
      height: 40px;
    }
  }
`;

const StyledSearchButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  padding: 0;
  height: 100%;
  width: 42px;
  opacity: 0;
  background-color: transparent;
  cursor: pointer;
  z-index: 99;
`;

const StyledDropdown = styled.div`
  position: absolute;
  top: 50px;
  left: 0;
  width: 100%;
  min-width: 500px;
  min-height: 50px;
  border: 1px solid ${ props => props.theme.products.colors.border };
  padding: ${ props => props.padding ? props.padding : 0 };
  z-index: 100;
  font-size: 1em;
  background-color: ${ props => props.theme.colors.white };
  color: ${ props => props.theme.colors.darkGray };

  .cds--loading {
    max-width: 2rem;
    max-height: 2rem;
  }

  .products {
    max-height: 375px;
    overflow: auto;
  }

  .categories {
    overflow: auto;
    min-height: 20px;
    max-height: 100px;
    flex-grow: 1;
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    top: 45px;
    min-width: 100%;
  }
`;

const StyledLink = styled(Link)`
  margin: 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.25em;
  color: inherit;
`;

const StyledRow = styled.div`
  font-size: 0.875rem;
  height: 40px;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${ props => props.theme.colors.shadowGray };
  color: ${ props => props.theme.colors.darkGray };

  > p {
    font-weight: 600;
  }

  .cds--checkbox-wrapper {
    cursor: pointer;
    flex-grow: 0;

    label {
      &:before {
        width: 14px;
        height: 14px;
        background-color: ${ props => props.theme.colors.white };
      }
      &:after {
        top: 7px;
        left: 6px;
      }

      color: inherit;
    }
  }
`;

const StyledProductSearchItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  height: 125px;
  box-sizing: border-box;
  max-height: 125px;
  border-bottom: 2px solid ${ props => props.theme.colors.shadowGray };

  .preview {
    min-width: 70px;
    max-width: 70px;
    height: 70px;
    padding: 7px;
    margin-right: 1rem;
    border: 1px solid ${ props => props.theme.colors.shadowGray };
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;

    a {
      display: block;
      width: 100%;
      max-height: 100%;

      svg {
        position: relative;
        display: block;
        margin: 0 auto;
        height: auto;
        width: auto;
        max-height: 100%;
        max-width: 100%;
      }
    }

    img {
      display: block;
      width: auto;
      max-width: 100%;
      max-height: 55px;
      margin: 0 auto;
    }
  }

  .itemRight {
    flex-grow: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    max-width: calc(100% - 70px - 1rem);
  }

  .details {
    flex-grow: 1;
    padding-right: 1rem;
    font-size: 0.875rem;
    color: ${ props => props.theme.colors.darkGray };
    overflow: hidden;

    .title {
      font-size: inherit;
      line-height: 1.35rem;
      margin: 0;
      a {
        color: inherit;
      }
    }

    .code, .stock {
      font-weight: 600;
      font-size: inherit;

      span {
        margin: 0 5px;
        font-weight: normal;
      }
    }

    .stock {
      padding-bottom: 5px;

      &.inStock {
        span {
          color: ${ props => props.theme.products.colors.inStock };
        }
      }
      &.outOfStock {
        span {
          text-transform: none;
          color: ${ props => props.theme.colors.default };
        }
      }
      &.onDemand {
        span {
          color: ${ props => props.theme.colors.darkGray };
          text-transform: none;
        }
      }
      span {
        text-transform: lowercase;
      }

      .react-tooltip {
        span {
          text-transform: none;
        }
      }
    }

    .deliveryEstimate {
      line-height: 1.5;
    }
  }

  .priceContainer {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-width: 130px;

    .price {
      margin-bottom: 5px;

      .oldPrice {
        color: ${ props => props.theme.colors.gray };
        font-size: 0.75rem;
      }

      .currentPrice {
        color: ${ props => props.theme.colors.default };
        font-size: 0.875rem;
      }
    }

    .stockAlert {
      max-width: 130px;
      min-width: 110px;
      width: 100%;
      min-height: 0;
      height: 30px;
      font-size: 0.75rem;
      border: 1px solid ${ props => props.theme.colors.default };
      color: ${ props => props.theme.colors.default };
      stroke: ${ props => props.theme.colors.default };
      background-color: transparent;
    }

    .addToCart {
      max-width: 130px;
      min-width: 110px;
      height: 30px;
      font-size: 0.75rem;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    height: auto;
    max-height: fit-content;

    .preview {
      align-self: start;
    }

    .itemRight {
      flex-direction: column;
    }

    .priceContainer {
      margin: 0;
      align-items: flex-start;

      .price {
        align-items: flex-start;

        .vatInfo {
          margin-top: 0;
        }
      }
    }
  }
`;

const StyledCategorySearchItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 1rem;
  color: ${ props => props.theme.colors.darkGray };
  font-size: 0.875rem;

  a {
    margin: 0;
    color: inherit;
    line-height: 1.5;
  }

  .parentPath {
    font-size: inherit;
    max-width: 60%;
    text-align: right;
    color: ${ props => props.theme.colors.gray };
  }
`;

const StyledInlineLoading = styled(InlineLoading)`
  padding: 0 1rem;
  min-height: 0;
  margin: ${ props => props.status === 'active' || props.status === 'error' ? '0.875rem 0' : 0 };
`;

const SearchComponent = memo(forwardRef((props, ref) => {
  const {
    currency,
    withVat,
    onClick,
    addToCartLoading,
    searchResults,
    search,
    isDropdownOpen,
    isLoading,
    onlyAvailable,
    isValid,
    onEnter,
    onAddToCart,
    onStockAlertModalOpen,
    onToggleAvailable,
    onSearchClick,
    onKeyDown,
    onChange,
    onClear,
    accountManagerPhone,
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();

  const renderProducts = products => products.map(product => (
    <StyledProductSearchItem key={ product.id } theme={ theme }>
      <div className="preview">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          to={ `products/${ product.slug }/pd/${ product.id }` }
          onClick={ onClick }
          state={ {
            component: 'search',
            source: 'search',
            clickedOn: 'image',
            productId: product.id
          } }
        >
          <Img
            src={ product?.pictures?.at(0)?.urlOverlayPicture }
            unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
            loading="lazy"
            loader={ <Loading active withOverlay={ false } small /> }
            alt={ product.title }
          />
        </Link>
      </div>
      <div className="itemRight">
        <div className="details">
          <StyledLink
            className="title"
            to={ `products/${ product.slug }/pd/${ product.id }` }
            onClick={ onClick }
            state={ {
              component: 'search',
              source: 'search',
              clickedOn: 'title',
              productId: product.id
            } }
          >
            { highlightText(product.title, product?.highlighted?.title) }
          </StyledLink>
          <p className="code">{ t('product.code') }:<span>{ product.code }</span></p>
          { !product.warnings?.blocked_stocks && (
            renderSearchStock({ t, product, info: { accountManagerPhone } })
          ) }
          { (!product?.hasPrice || product?.hasSupplierOffers) && (
            <DeliveryEstimate
              estimate={ product?.supplierOfferDeliveryTimeDays }
              isExternalSupplier={ !product?.hasPrice }
            />
          ) }
        </div>
        <div className="priceContainer">
          { (!product?.hasPriceRequest && !product.warnings?.blocked_prices) && (
            <Price
              price={ product.price }
              promoPrice={ product.promoPrice }
              currency={ currency || product.currency }
              withVat={ withVat }
              displayVatInfo
            />
          ) }
          { /* eslint-disable no-nested-ternary */ }
          { product?.hasPriceRequest ? <ProductPriceRequest productId={ product.id } /> : (
            ((product.stockValue === 0 || product.stockValue < product.minQuantity) && !Object.keys(product?.warnings || {}).length)
              ? (
                <Button
                  data-test="onStockAlert"
                  data-testid={ product.id || product.productId }
                  className="red stockAlert"
                  kind="danger--ghost"
                  onClick={ () => onStockAlertModalOpen(product) }
                >
                  <Megaphone />
                  { t('products.stockAlert') }
                </Button>
              )
              : (
                <AddToCart
                  onClick={ () => onAddToCart(product) }
                  trackingContext={ {
                    location: 'product_search',
                    searched_term: search,
                    product_sku: product?.code,
                  } }
                  loading={ addToCartLoading === product.id }
                  disabled={
                    addToCartLoading === product.id
                    || product.warnings?.blocked_prices
                    || product.warnings?.blocked_stocks
                    || product.warnings?.blocked_orders
                  }
                  showSelector={ false }
                  id={ product.id }
                  size="md"
                />
              )
          )}
        </div>
      </div>
    </StyledProductSearchItem>
  ));

  const renderCategories = categories => categories.map(category => (
    <StyledCategorySearchItem key={ category.id } theme={ theme }>
      <StyledLink
        to={ `/products/${ category.slug }-c${ category.id }/c` }
        onClick={ onClick }
      >
        { category.name }
      </StyledLink>
      { category.parentPath && (
        <p className="parentPath">{ category.parentPath }</p>
      ) }
    </StyledCategorySearchItem>
  ));

  const memoizedProducts = useMemo(
    () => {
      if (searchResults?.products?.length) {
        return renderProducts(searchResults.products);
      }
      return null;
    },
    [searchResults?.products, addToCartLoading]
  );

  const memoizedCategories = useMemo(
    () => {
      if (searchResults?.categories?.length) {
        return renderCategories(searchResults.categories);
      }
      return null;
    },
    [searchResults?.categories]
  );

  return (
    <StyledSearchComponent ref={ ref }>
      <StyledSearch
        theme={ theme }
        renderIcon={ () => <SearchIcon size={ 24 } /> }
        id={ uniqueId('search_global') }
        labelText=""
        light
        value={ search }
        placeholder={ t('common.search.placeholder') }
        onClick={ onSearchClick }
        onKeyDown={ onKeyDown }
        onChange={ onChange }
        onClear={ onClear }
      />
      <StyledSearchButton role="button" onClick={ onEnter } />
      { (isDropdownOpen) && (
        <StyledDropdown theme={ theme }>
          <StyledRow theme={ theme }>
            <p>{ t('common.search.placeholder') }</p>
            <Checkbox
              id="onlyAvailableProducts"
              labelText={ t('common.search.onlyAvailable') }
              onChange={ (_, { checked }) => onToggleAvailable(checked) }
              checked={ onlyAvailable }
            />
          </StyledRow>
          { !isValid && (
            <p style={{ padding: '1rem' }}>{ t('common.search.minSearchLength') }</p>
          ) }
          {/* eslint-disable no-nested-ternary */}
          <StyledInlineLoading
            status={ isLoading ? 'active' : searchResults?.error ? 'error' : null }
            description={ isLoading ? t('common.loading_products') : searchResults?.error || '' }
          />
          {/* eslint-enable no-nested-ternary */}
          { memoizedProducts?.length > 0 && (
            <div className="products">
              { memoizedProducts }
            </div>
          ) }
          { memoizedCategories?.length > 0 && (
            <>
              <StyledRow theme={ theme }>
                <span>{ t('common.categories') }</span>
              </StyledRow>
              <div className="categories">
                { memoizedCategories }
              </div>
            </>
          ) }
        </StyledDropdown>
      ) }
    </StyledSearchComponent>
  );
}));

SearchComponent.propTypes = {
  isValid: bool,
  addToCartLoading: oneOfType([bool, string]),
  currency: string,
  withVat: bool,
  onKeyDown: func,
  onClick: func,
  onChange: func,
  onEnter: func,
  onToggleAvailable: func,
  onAddToCart: func,
  onStockAlertModalOpen: func,
  onSearchClick: func,
  search: string,
  isDropdownOpen: bool,
  searchResults: object,
  isLoading: bool,
  onlyAvailable: bool,
  onClear: func,
  accountManagerPhone: string,
};
SearchComponent.displayName = 'SearchComponent';

export default SearchComponent;
